import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', path: '/homepage', icon: 'Home' },
    { name: 'About', path: '/about-vastech', icon: 'Building2' },
    {
      name: 'Services',
      path: '#',
      icon: 'Settings',
      isDropdown: true,
      dropdownItems: [
        { name: 'AC Maintenance', path: '/ac-maintenance-services', icon: 'Wind', description: 'Predictive HVAC maintenance solutions' },
        { name: 'Elevator Solutions', path: '/elevator-solutions', icon: 'ArrowUp', description: 'Precision-engineered vertical mobility' },
        { name: 'Smart Home Services', path: '/smart-home-services', icon: 'Home', description: 'Intelligent home automation systems' },
      ]
    },
    { name: 'Portfolio', path: '/project-portfolio', icon: 'FolderOpen' },
    { name: 'Contact', path: '/contact-consultation', icon: 'Phone' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  };

  const toggleServices = (e) => {
    e.preventDefault();
    setIsServicesOpen(!isServicesOpen);
  };

  const toggleMobileServices = (e) => {
    e.preventDefault();
    setIsMobileServicesOpen(!isMobileServicesOpen);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const isServiceActive = () => {
    const servicePaths = ['/ac-maintenance-services', '/elevator-solutions', '/smart-home-services'];
    return servicePaths.includes(location?.pathname);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-elevation-medium border-b border-border'
          : 'bg-transparent'
        }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/homepage"
            className="flex items-center space-x-3 group flex-shrink-0"
            onClick={closeMenu}
          >
            <div className="relative">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <Icon name="Zap" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse"></div>
            </div>
            <div className="block">
              <img
                src="/assets/images/logo.png"
                alt="VASTECH Elevate"
                className="h-8 w-auto max-w-[120px] sm:max-w-none"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => {
              if (item?.isDropdown) {
                return (
                  <div key={item?.name} className="relative" ref={servicesRef}>
                    <button
                      onClick={toggleServices}
                      onMouseEnter={() => setIsServicesOpen(true)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 mechanical-precision ${isServiceActive()
                          ? 'bg-primary text-primary-foreground shadow-elevation-subtle'
                          : 'text-text-secondary hover:text-primary hover:bg-muted'
                        }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.name}</span>
                      <Icon
                        name="ChevronDown"
                        size={14}
                        className={`transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={`absolute top-full left-0 mt-2 w-80 bg-white/95 backdrop-blur-md border border-border rounded-xl shadow-elevation-medium transition-all duration-300 ${isServicesOpen
                          ? 'opacity-100 visible translate-y-0'
                          : 'opacity-0 invisible translate-y-2'
                        }`}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      <div className="p-2">
                        {item?.dropdownItems?.map((dropdownItem) => (
                          <Link
                            key={dropdownItem?.path}
                            to={dropdownItem?.path}
                            onClick={closeMenu}
                            className={`flex items-start space-x-3 p-4 rounded-lg transition-all duration-300 mechanical-precision group ${isActivePath(dropdownItem?.path)
                                ? 'bg-primary text-primary-foreground shadow-elevation-subtle'
                                : 'text-text-secondary hover:text-primary hover:bg-muted'
                              }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mt-1 ${isActivePath(dropdownItem?.path)
                                ? 'bg-white/20'
                                : 'bg-accent/10 text-accent group-hover:bg-accent/20'
                              }`}>
                              <Icon name={dropdownItem?.icon} size={16} />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">{dropdownItem?.name}</div>
                              <div className={`text-xs mt-1 ${isActivePath(dropdownItem?.path)
                                  ? 'text-primary-foreground/70'
                                  : 'text-text-secondary/70'
                                }`}>
                                {dropdownItem?.description}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 mechanical-precision ${isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-elevation-subtle'
                      : 'text-text-secondary hover:text-primary hover:bg-muted'
                    }`}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3 flex-shrink-0">
            <Link to="/contact-consultation">
              <Button
                variant="default"
                size="sm"
                iconName="Calendar"
                iconPosition="left"
                className="bg-accent hover:bg-accent/90 mechanical-precision"
              >
                Schedule Assessment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-300 mechanical-precision flex-shrink-0 ml-2"
            aria-label="Toggle menu"
          >
            <Icon
              name={isMenuOpen ? "X" : "Menu"}
              size={24}
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${isMenuOpen
              ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            }`}
        >
          <div className="bg-white/95 backdrop-blur-md border-t border-border w-full">
            <nav className="px-4 py-4 space-y-2 w-full">
              {navigationItems?.map((item) => {
                if (item?.isDropdown) {
                  return (
                    <div key={item?.name} className="space-y-2">
                      <button
                        onClick={toggleMobileServices}
                        className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 mechanical-precision ${isServiceActive()
                            ? 'bg-primary text-primary-foreground shadow-elevation-subtle'
                            : 'text-text-secondary hover:text-primary hover:bg-muted'
                          }`}
                      >
                        <div className="flex items-center space-x-3 w-full">
                          <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                          <span className="flex-1 break-words text-left">{item?.name}</span>
                        </div>
                        <Icon
                          name="ChevronDown"
                          size={16}
                          className={`transition-transform duration-200 flex-shrink-0 ${isMobileServicesOpen ? 'rotate-180' : ''
                            }`}
                        />
                      </button>

                      {/* Mobile Dropdown Items */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ${isMobileServicesOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        <div className="ml-4 space-y-1 border-l border-border pl-4">
                          {item?.dropdownItems?.map((dropdownItem) => (
                            <Link
                              key={dropdownItem?.path}
                              to={dropdownItem?.path}
                              onClick={closeMenu}
                              className={`flex items-start space-x-3 px-4 py-3 rounded-lg transition-all duration-300 mechanical-precision group ${isActivePath(dropdownItem?.path)
                                  ? 'bg-primary text-primary-foreground shadow-elevation-subtle'
                                  : 'text-text-secondary hover:text-primary hover:bg-muted'
                                }`}
                            >
                              <div className={`w-6 h-6 rounded-lg flex items-center justify-center mt-0.5 flex-shrink-0 ${isActivePath(dropdownItem?.path)
                                  ? 'bg-white/20'
                                  : 'bg-accent/10 text-accent group-hover:bg-accent/20'
                                }`}>
                                <Icon name={dropdownItem?.icon} size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm break-words">{dropdownItem?.name}</div>
                                <div className={`text-xs mt-1 break-words ${isActivePath(dropdownItem?.path)
                                    ? 'text-primary-foreground/70'
                                    : 'text-text-secondary/70'
                                  }`}>
                                  {dropdownItem?.description}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={closeMenu}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 mechanical-precision ${isActivePath(item?.path)
                        ? 'bg-primary text-primary-foreground shadow-elevation-subtle'
                        : 'text-text-secondary hover:text-primary hover:bg-muted'
                      }`}
                  >
                    <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                    <span className="flex-1 break-words">{item?.name}</span>
                  </Link>
                );
              })}

              <div className="pt-4">
                <Link to="/contact-consultation" onClick={closeMenu}>
                  <Button
                    variant="default"
                    fullWidth
                    iconName="Calendar"
                    iconPosition="left"
                    className="bg-accent hover:bg-accent/90 mechanical-precision"
                  >
                    Schedule Assessment
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;